import { Injectable, OnDestroy } from '@angular/core';
import { AutomationReplacementChipsDataService, PagedResponse } from '../common/components';
import { forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { CampaignSelectorService, SimpleCampaign } from './campaign-selector.service';
import { YeswareCampaign, YeswareCampaignSelectorService } from './yesware-campaign-selector.service';

// Common interface to represent campaigns from different sources
export interface CombinedCampaign {
  id: string;
  name: string;
  source: 'campaigns' | 'yesware';
}

@Injectable({ providedIn: 'root' })
export class CombinedCampaignSelectorService
  implements AutomationReplacementChipsDataService<CombinedCampaign>, OnDestroy
{
  supportsSearching = false;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly campaignSelectorService: CampaignSelectorService,
    private readonly yeswareCampaignSelectorService: YeswareCampaignSelectorService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  convertDataToDisplay(campaign: CombinedCampaign): Observable<string> {
    return of(campaign.name || '');
  }

  convertDataToFormValue(campaign: CombinedCampaign): any {
    return `${campaign.id}`;
  }

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    extraData: any,
  ): Observable<PagedResponse<CombinedCampaign>> {
    const cursorData = this.parseCursor(cursor);

    if (cursorData.source === 'yesware' && cursorData.hasMoreInCampaigns === false) {
      // Only fetch from yesware if we've exhausted campaigns results
      return this.yeswareCampaignSelectorService.doLookup(searchTerm, cursorData.cursor, pageSize, extraData).pipe(
        map((response) => this.mapYeswareResponseToCombined(response, false)),
        catchError((err) => {
          console.error('Error fetching from Yesware:', err);
          return of({
            results: [],
            nextCursor: '',
            hasMore: false,
          });
        }),
      );
    } else {
      // First fetch from campaign service
      return this.campaignSelectorService
        .doLookup(searchTerm, cursorData.source === 'campaigns' ? cursorData.cursor : '', pageSize, extraData)
        .pipe(
          switchMap((campaignsResponse) => {
            // If we got fewer results than requested and there might be more in Yesware
            if (campaignsResponse.results.length < pageSize) {
              // Get remaining items needed from Yesware
              const remainingSize = pageSize - campaignsResponse.results.length;
              return this.yeswareCampaignSelectorService.doLookup(searchTerm, '', remainingSize, extraData).pipe(
                catchError((err) => {
                  console.error('Error fetching from Yesware:', err);
                  return of({
                    results: [],
                    nextCursor: '',
                    hasMore: false,
                  });
                }),
                map((yeswareResponse) => {
                  const combinedResponse: PagedResponse<CombinedCampaign> = {
                    results: [
                      ...campaignsResponse.results.map((campaign) => ({
                        id: campaign.id,
                        name: campaign.name,
                        source: 'campaigns' as const,
                      })),
                      ...yeswareResponse.results.map((campaign) => ({
                        id: campaign.campaign_id,
                        name: campaign.name,
                        source: 'yesware' as const,
                      })),
                    ],
                    nextCursor: yeswareResponse.hasMore
                      ? this.createCursor('yesware', yeswareResponse.nextCursor, false)
                      : '',
                    hasMore: yeswareResponse.hasMore,
                  };
                  return combinedResponse;
                }),
              );
            }

            // If we got full page size from campaigns, just return those results
            return of(this.mapCampaignsResponseToCombined(campaignsResponse));
          }),
          catchError(() => {
            // If campaigns service fails, try yesware
            return this.yeswareCampaignSelectorService.doLookup(searchTerm, '', pageSize, extraData).pipe(
              map((response) => this.mapYeswareResponseToCombined(response, false)),
              catchError((err) => {
                console.error('Error fetching from Yesware:', err);
                return of({
                  results: [],
                  nextCursor: '',
                  hasMore: false,
                } as PagedResponse<CombinedCampaign>);
              }),
            );
          }),
        );
    }
  }

  getMulti(campaignIds: string[]): Observable<CombinedCampaign[]> {
    if (!campaignIds || campaignIds.length === 0) {
      return of([]);
    }

    // Try to fetch from both services with the full list of IDs
    return forkJoin({
      campaigns: this.campaignSelectorService.getMulti(campaignIds).pipe(
        take(1),
        catchError(() => of([] as SimpleCampaign[])),
      ),
      yesware: this.yeswareCampaignSelectorService.getMulti(campaignIds).pipe(
        take(1),
        catchError(() => of([] as YeswareCampaign[])),
      ),
    }).pipe(
      map(({ campaigns, yesware }) => {
        const combinedResults: CombinedCampaign[] = [];
        const foundIds = new Set<string>();

        // Add campaigns results first
        campaigns.forEach((campaign) => {
          if (campaign.id) {
            combinedResults.push({
              id: campaign.id,
              name: campaign.name,
              source: 'campaigns',
            });
            foundIds.add(campaign.id);
          }
        });

        // Add yesware campaigns that weren't already found in campaigns service
        yesware.forEach((campaign) => {
          if (!foundIds.has(campaign.campaign_id) && campaign.campaign_id) {
            combinedResults.push({
              id: campaign.campaign_id,
              name: campaign.name,
              source: 'yesware',
            });
          }
        });

        return combinedResults;
      }),
    );
  }

  // Helper method to convert a campaigns response to the combined format
  private mapCampaignsResponseToCombined(response: PagedResponse<SimpleCampaign>): PagedResponse<CombinedCampaign> {
    return {
      results: response.results.map((campaign) => ({
        id: campaign.id,
        name: campaign.name,
        source: 'campaigns',
      })),
      nextCursor: response.hasMore ? this.createCursor('campaigns', response.nextCursor, true) : 'yesware::0', // When campaigns has no more, start from beginning of yesware
      hasMore: true, // Always true when transitioning to yesware
    };
  }

  // Helper method to convert a yesware response to the combined format
  private mapYeswareResponseToCombined(
    response: PagedResponse<YeswareCampaign>,
    hasMoreInCampaigns: boolean,
  ): PagedResponse<CombinedCampaign> {
    return {
      results: response.results.map((campaign) => ({
        id: campaign.campaign_id,
        name: campaign.name,
        source: 'yesware',
      })),
      nextCursor: response.hasMore ? this.createCursor('yesware', response.nextCursor, hasMoreInCampaigns) : '',
      hasMore: response.hasMore,
    };
  }

  // Create a cursor string that encodes which service to use next and its cursor
  private createCursor(source: 'campaigns' | 'yesware', cursor: string | null, hasMoreInCampaigns: boolean): string {
    return `${source}:${cursor || ''}:${hasMoreInCampaigns ? '1' : '0'}`;
  }

  // Parse a cursor string back into its components
  private parseCursor(cursor: string | null): {
    source: 'campaigns' | 'yesware';
    cursor: string;
    hasMoreInCampaigns: boolean;
  } {
    if (!cursor) {
      return {
        source: 'campaigns',
        cursor: '',
        hasMoreInCampaigns: true,
      };
    }

    const parts = cursor.split(':');
    if (parts.length < 2) {
      return {
        source: 'campaigns',
        cursor: '',
        hasMoreInCampaigns: true,
      };
    }

    const source = parts[0] as 'campaigns' | 'yesware';
    const cursorValue = parts[1] || '';
    const hasMoreInCampaigns = parts.length > 2 ? parts[2] === '1' : true;

    return {
      source: source === 'yesware' ? 'yesware' : 'campaigns',
      cursor: cursorValue,
      hasMoreInCampaigns,
    };
  }
}
